import React, { useEffect, useMemo, useState } from "react";
import { LinkProps } from "next/link";
import { calculateAbTestId } from "domains/auth/misc";
import { useScrollbarSize } from "domains/commons/hooks/useScrollbarSize";
import isBreakpointMobile from "domains/commons/isBreakpointMobile";
import TopBar from "domains/navigation/components/TopBar";
import { ERROR_TO_CANCEL_NAVIGATION } from "domains/navigation/constants/Navigation";
import useRouter from "domains/navigation/hooks/useRouter";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import { useUserContext } from "domains/user/contexts/UserProvider";
import posthog from "posthog-js";

import {
  chakra,
  Flex,
  Progress,
  useBreakpoint,
  useColorMode,
  VStack,
} from "@chakra-ui/react";
import * as Sentry from "@sentry/browser";

import "react-image-gallery/styles/css/image-gallery.css";

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children = <></> }: LayoutProps) {
  const { setColorMode } = useColorMode();
  const router = useRouter();
  const { user, isUnsigned } = useUserContext();
  const breakpoint = useBreakpoint({ ssr: true });
  const scrollbarSize = useScrollbarSize();
  const { selectedTeam, selectedProject, hasNoProject } = useTeamContext();
  const isMobile = isBreakpointMobile(breakpoint);
  const [isLoading, setIsLoading] = useState(false);

  const isHiddenTopBar = useMemo(() => {
    return [
      "/compose/new",
      "/login",
      "/unsubscribe",
      "/canvas/[id]",
      "/see-you-soon",
      "/down",
    ].includes(router.pathname);
  }, [router.pathname]);

  // ----------------------------------

  useEffect(() => {
    setColorMode("dark");
  }, [setColorMode]);

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    const abTestId = calculateAbTestId(user);

    posthog.identify(user.id, {
      id: user.id,
      username: user.username,
      name: user.fullName,
      email: user.email,
      abTestId: abTestId,
      createdAt: user.createdAt,
      /**
       * @TODO: This is a temporary solution to track the persona of the user.
       * When we will handle persona determination, we will have to update this.
       *
       * Being set here, the persona should come to all events.
       *
       * That being mentioned, for some events where we want to track the persona even when the user
       * is not authenticated, we will have to set the persona in the event tracking. This includes
       * Tools and Home carousel.
       */
      persona: "default",
    });

    Sentry.setUser({
      email: user.email,
      id: user.id,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (!selectedTeam.id || !selectedProject.id) {
      return;
    }
    posthog.group("teamId", selectedTeam.id, {
      name: selectedTeam.name,
      plan: selectedTeam.plan,
      usersCount: selectedTeam.usersCount,
      projectsCount: selectedTeam.projects.length,
    });
    posthog.group("projectId", selectedProject.id, {
      name: selectedProject.name,
      teamId: selectedProject.teamId,
      usersCount: selectedProject.usersCount,
      apiKeysCount: selectedProject.apiKeys.length,
    });
  }, [
    selectedTeam.id,
    selectedProject.id,
    selectedTeam.name,
    selectedTeam.plan,
    selectedTeam.usersCount,
    // the "?" is very important, it looks like it can sometimes be undefined https://scenario3d.sentry.io/issues/6691267609/?project=4504254817370112&referrer=github_integration
    selectedTeam.projects?.length,
    selectedProject.name,
    selectedProject.teamId,
    selectedProject.usersCount,
    // the "?" is very important, it looks like it can sometimes be undefined https://scenario3d.sentry.io/issues/6691267609/?project=4504254817370112&referrer=github_integration
    selectedProject.apiKeys?.length,
  ]);

  useEffect(() => {
    // when we change the route, we check if the teamId is present in the new url
    // if not, we add it to the url and redirect to the new url
    // also throw an error to stop the first route change
    const handleStart = (path: string) => {
      const [base, params = ""] = path.split("?");

      // A new URLSearchParams object allows for easier manipulation of URL parameters
      const urlParams = new URLSearchParams(params);
      const isMissingTeamId = !urlParams.has("teamId");
      const isMissingProjectId = !urlParams.has("projectId");
      const isSameProjectId =
        !!selectedProject.id && selectedProject.teamId === selectedTeam.id;
      let isChanged = false;

      if (
        (isMissingTeamId || (isMissingProjectId && isSameProjectId)) &&
        base !== "/login" &&
        base !== "/see-you-soon" &&
        !isUnsigned
      ) {
        isChanged = true;
        if (isMissingTeamId) {
          urlParams.append("teamId", selectedTeam.id.toString());
        }
        if (isMissingProjectId && isSameProjectId) {
          urlParams.append("projectId", selectedProject.id.toString());
        }
      }

      if (isUnsigned && (!isMissingTeamId || !isMissingProjectId)) {
        isChanged = true;
        if (!isMissingTeamId) {
          urlParams.delete("teamId");
        }
        if (!isMissingProjectId) {
          urlParams.delete("projectId");
        }
      }

      if (isChanged) {
        let baseWithoutLocale = "";
        // regex to match / then 2 letters (locale) then / or end of string
        if (base.match(/^\/[a-z]{2}(\/|$)/)) {
          baseWithoutLocale = base.replace(/^\/[a-z]{2}(\/|$)/, "");
          if (!baseWithoutLocale.startsWith("/")) {
            baseWithoutLocale = `/${baseWithoutLocale}`;
          }
        } else {
          baseWithoutLocale = base;
        }

        const newPath = `${baseWithoutLocale}?${urlParams.toString()}`;
        void router.nextRouterPush(newPath as LinkProps["href"]);
        throw ERROR_TO_CANCEL_NAVIGATION;
      }

      setIsLoading(true);
    };

    const handleStop = () => {
      setIsLoading(false);
    };

    router.events.on("routeChangeStart", handleStart);
    router.events.on("routeChangeComplete", handleStop);
    router.events.on("hashChangeComplete", handleStop);
    router.events.on("routeChangeError", handleStop);

    return () => {
      router.events.off("routeChangeStart", handleStart);
      router.events.off("routeChangeComplete", handleStop);
      router.events.off("hashChangeComplete", handleStop);
      router.events.off("routeChangeError", handleStop);
    };
  }, [
    router,
    selectedTeam.id,
    selectedProject.teamId,
    selectedProject.id,
    isUnsigned,
  ]);

  // Redirect if no project
  useEffect(() => {
    if (!router.isReady) return;
    if (isUnsigned) return;
    if (
      hasNoProject &&
      ![
        "/",
        "/404",
        "/down",
        "/forbidden",
        "/login",
        "/payment/failure",
        "/payment/success",
        "/profile",
        "/recent-tasks",
        "/see-you-soon",
        "/subscription/confirm",
        "/subscription/failure",
        "/subscription/success",
        "/team",
        "/tier",
        "/unsubscribe",
      ].includes(router.pathname)
    ) {
      void router.replace({
        pathname: "/",
        query: {
          teamId: selectedTeam.id,
        },
      });
    }
  }, [isUnsigned, hasNoProject, router, selectedTeam.id]);

  // ----------------------------------

  return (
    <Flex
      direction={isMobile ? "column" : "row"}
      flex={1}
      w="100%"
      minW="100%"
      minH="100vh"
      m="0"
    >
      {isLoading && (
        <Progress
          pos="absolute"
          zIndex="overlay"
          top={0}
          w={`calc(100vw - ${scrollbarSize.width}px)`}
          isIndeterminate
          size="sm"
          variant="default"
        />
      )}

      <chakra.main w="100%" overflowX="clip" minH="100vh">
        <VStack align="stretch" h="full" spacing={0}>
          {!isHiddenTopBar && <TopBar />}
          <VStack
            align="stretch"
            w="full"
            h="full"
            pt={isHiddenTopBar ? "0px" : "56px"}
            spacing={0}
          >
            {children}
          </VStack>
        </VStack>
      </chakra.main>
    </Flex>
  );
}
